import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ArrowUpward from '@mui/icons-material/esm/ArrowUpward';
import ArrowDownward from '@mui/icons-material/esm/ArrowDownward';

export interface KPICardProps {
  title: string;
  value: string | number;
  change?: number; // Percentage change (positive or negative)
  changeLabel?: string; // e.g., "vs last month", "vs plan"
  icon?: React.ReactNode;
  chart?: React.ReactNode; // Optional mini-chart component
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  onClick?: () => void;
  loading?: boolean;
}

export default function KPICard({
  title,
  value,
  change,
  changeLabel,
  icon,
  chart,
  color = 'primary',
  onClick,
  loading = false,
}: KPICardProps) {
  const isPositiveChange = change !== undefined && change >= 0;
  const changeColor = isPositiveChange ? 'success.main' : 'error.main';

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        {/* Header with title and icon */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" fontWeight="medium">
            {title}
          </Typography>
          {icon && (
            <Box sx={{ color: `${color}.main` }}>
              {icon}
            </Box>
          )}
        </Box>

        {/* Main value */}
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          {loading ? '...' : value}
        </Typography>

        {/* Change indicator */}
        {change !== undefined && !loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: chart ? 2 : 0 }}>
            {isPositiveChange ? (
              <ArrowUpward sx={{ fontSize: 16, color: changeColor }} />
            ) : (
              <ArrowDownward sx={{ fontSize: 16, color: changeColor }} />
            )}
            <Typography variant="body2" sx={{ color: changeColor, fontWeight: 'medium' }}>
              {Math.abs(change)}%
            </Typography>
            {changeLabel && (
              <Typography variant="caption" color="text.secondary">
                {changeLabel}
              </Typography>
            )}
          </Box>
        )}

        {/* Optional mini-chart */}
        {chart && !loading && (
          <Box sx={{ mt: 2, height: 60 }}>
            {chart}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
